﻿import { useState, useEffect, useMemo, useRef } from "react";
import { useSelector } from "react-redux";
import fileDownload from "js-file-download";
import { useNavigate } from "react-router-dom";
import { Modal, Button, Table, Col, Row } from "react-bootstrap";
import _ from "lodash";

import Pagination from "@/components/Pagination/Pagination";
import Fetching from "@/components/Fetching/Fetching";

import { formatMilesDecimalNew } from "@/services/Utils";
import api from "@/services/api.service";

import classes from "./dashboard-valorization.module.scss";

const DashboardValorizationTable = ({ filters, showTable, setShowTable }) => {
	const refFile = useRef(null);
	const navigate = useNavigate();
	const { estate_parent_id, estate_id, type_currency } = useSelector((state) => state.auth.filter);

	const [fetching, setFetching] = useState(true);
	const [mainSearch, setMainSearch] = useState("");
	const [search, setSearch] = useState("");
	const [paginate, setPaginate] = useState(8);
	const [page, setPage] = useState(1);
	const [quantityElements, setQuantityElements] = useState(0);
	const [lastPage, setLastPage] = useState(0);
	const [fromElements, setFromElements] = useState(0);
	const [toElements, setToElements] = useState(0);
	const [defaultUnit, setDefaultUnit] = useState(1000);
	const [elements, setElements] = useState([]);

	useEffect(() => {
		setFetching(true);
		api.get("/client/dashboard/valorization/table", {
			...filters,
			estate_parent_id,
			estate_id,
			type_currency,
			search,
			page,
			paginate,
			defaultUnit,
		}).then((response) => {
			updateData(response);
		});
	}, [search, page, paginate, estate_parent_id, estate_id, type_currency, defaultUnit, filters]);

	const updateData = (data) => {
		setQuantityElements(data.total);
		setFromElements(data.from);
		setToElements(data.to);
		setLastPage(data.last_page);
		setElements(data.data);
		setFetching(false);
	};

	const handleExport = () => {
		setFetching(true);
		api.getExcel("/client/dashboard/valorization/export", {
			...filters,
			estate_parent_id,
			estate_id,
			type_currency,
			defaultUnit,
		}).then((response) => {
			const date = new Date();

			const filename = `Productos sin valorizar  ${date.getDate()}-${date.getMonth() + 1}-${date.getFullYear()}.xlsx`;
			fileDownload(response, filename);

			setFetching(false);
		});
	};

	const handleImport = () => {
		refFile.current.click();
	};

	const uploadFile = (e) => {
		const file = e.target.files[0];
		const formData = new FormData();
		formData.append("search", search);
		formData.append("page", page);
		formData.append("paginate", paginate);
		formData.append("estate_parent_id", estate_parent_id);
		formData.append("estate_id", estate_id);
		formData.append("type_currency", type_currency);
		formData.append("defaultUnit", defaultUnit);
		formData.append("file", file);

		setFetching(true);
		api.post("/client/dashboard/valorization/import", formData).then((response) => {
			if (refFile && refFile.current) {
				refFile.current.value = "";
			}

			if (response && Object.prototype.hasOwnProperty.call(response, "products")) {
				setFetching(false);
				setShowTable(false);

				window.location.reload();
			} else {
				setFetching(false);
			}
		});
	};

	const delayedQuery = useMemo(
		() =>
			_.debounce((value) => {
				setSearch(value);
			}, 500),
		[],
	);

	useEffect(() => {
		return () => {
			delayedQuery.cancel();
		};
	}, [delayedQuery]);

	const setDelaySearch = (value) => {
		setMainSearch(value);
		delayedQuery(value);
	};

	const setEntryPrice = (index) => {
		const item = elements[index];
		navigate(`/guias_despacho/editar/${item.order_delivery}`);
	};

	const handleClose = () => {
		navigate("/");
		setShowTable(false);
	};

	return (
		<Modal show={showTable} backdrop="static" keyboard={false} onHide={() => handleClose()} size="lg">
			<Modal.Header closeButton>
				<Modal.Title>Productos sin Valorizar</Modal.Title>
			</Modal.Header>

			<Modal.Body>
				<div style={{ width: "100%", marginBottom: "10px" }}>
					<Row>
						<Col md={8} sm={8}>
							<Pagination
								page={page}
								setPage={setPage}
								paginate={paginate}
								setPaginate={setPaginate}
								search={mainSearch}
								setSearch={setDelaySearch}
								quantityElements={quantityElements}
								LastPage={lastPage}
								FromElements={fromElements}
								ToElements={toElements}
								defaultUnit={defaultUnit}
								setDefaultUnit={setDefaultUnit}
							/>
						</Col>

						<Col md={4} sm={4} style={{ display: "flex" }}>
							<div className={classes.table_div}>
								<div
									className={`${classes.table_button} ${fetching && classes.fetching} ${classes.active}`}
									onClick={() => (fetching ? null : handleExport())}
								>
									Descargar Excel
								</div>
							</div>
							<div className={classes.table_div}>
								<div className={`${classes.table_button} ${fetching && classes.fetching}`} onClick={() => (fetching ? null : handleImport())}>
									Cargar Excel
								</div>
								<input style={{ display: "none" }} type="file" accept={`application/xlsx`} ref={refFile} onChange={(e) => uploadFile(e)} />
							</div>
						</Col>
					</Row>
				</div>

				{fetching ? (
					<Fetching topMargin="3em" bottomMargin="3em" />
				) : (
					<Table striped bordered hover className={classes.table_dashboard}>
						<thead>
							<tr>
								<th className={classes.th}>Producto</th>
								<th className={classes.th}>Cant. Envases</th>
								<th className={classes.th}>Contenido Total</th>
								<th className={classes.th}>UM</th>
								<th className={classes.th}>Distribuidor</th>
								<th className={classes.th}>N° Factura</th>
								<th className={classes.th}>N° Guía Despacho</th>
								<th className={classes.th} style={{ width: "15%" }}>
									Acción
								</th>
							</tr>
						</thead>
						<tbody>
							{elements.map((element, index) => {
								return (
									<tr key={index}>
										<td className={classes.td}>{element.tradename}</td>
										<td className={`${classes.td} ${classes.center}`}>{element.quantity && formatMilesDecimalNew(element.quantity)}</td>
										<td className={`${classes.td} ${classes.center}`}>
											{element.total_content && formatMilesDecimalNew(element.total_content)}
										</td>
										<td className={`${classes.td} ${classes.center}`}>{element.unit_measurement}</td>
										<td className={classes.td}>{element.dealer}</td>
										<td className={`${classes.td} ${classes.center}`}>{element.dealer_number}</td>
										<td className={`${classes.td} ${classes.center}`}>{element.dealer_delivery}</td>
										<td className={classes.td}>
											{element.order_delivery && (
												<Button variant="outline-success" size="sm" style={{ fontSize: "12px" }} onClick={() => setEntryPrice(index)}>
													Ingresar Precio
												</Button>
											)}
										</td>
									</tr>
								);
							})}
						</tbody>
					</Table>
				)}
			</Modal.Body>

			<Modal.Footer>
				<Button variant="secondary" onClick={() => handleClose()}>
					Cerrar
				</Button>
			</Modal.Footer>
		</Modal>
	);
};

export default DashboardValorizationTable;
